import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import AdminBreadcrumb from '../components/AdminBreadcrumb';
import AutocompleteSelect from '../components/AutocompleteSelect';
import { listSessions, createSession, revokeSession, renewSession, hardDeleteSession, fetchSessionOptions } from '../services/adminSessionService';
import { isSessionGateEnabled, setSessionGateEnabled } from '../services/sessionService';

const SESSION_TTL_OPTIONS = [
  { value: 30, label: 'session.ttl30min' },
  { value: 60, label: 'session.ttl1hour' },
  { value: 180, label: 'session.ttl3hours' },
  { value: 360, label: 'session.ttl6hours' },
  { value: 720, label: 'session.ttl12hours' },
  { value: 1440, label: 'session.ttl1day' },
  { value: 4320, label: 'session.ttl3days' },
  { value: 10080, label: 'session.ttl1week' },
  { value: 43800, label: 'session.ttl1month' },
];

const SessionManagerPage = () => {
  const { t } = useTranslation();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Global toggle state
  const [gateEnabled, setGateEnabled] = useState(true);
  const [toggling, setToggling] = useState(false);

  // Create form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newTtl, setNewTtl] = useState(60);
  const [creating, setCreating] = useState(false);

  // Scope state
  const [scopeType, setScopeType] = useState('all');
  const [selectedDistricts, setSelectedDistricts] = useState([]);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [availableOptions, setAvailableOptions] = useState({ districts: [], properties: [] });
  const [loadingOptions, setLoadingOptions] = useState(false);
  // Landing property selection
  const [landingPropertyId, setLandingPropertyId] = useState(null);

  // Created session result (to display token URL)
  const [createdSession, setCreatedSession] = useState(null);

  // Copy feedback
  const [copiedId, setCopiedId] = useState(null);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await listSessions();
      setSessions(data.sessions || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchGateConfig = useCallback(async () => {
    try {
      const enabled = await isSessionGateEnabled();
      setGateEnabled(enabled);
    } catch {
      // Default to true
      setGateEnabled(true);
    }
  }, []);

  const fetchOptions = useCallback(async () => {
    setLoadingOptions(true);
    try {
      const data = await fetchSessionOptions();
      setAvailableOptions({
        districts: data.districts || [],
        properties: data.properties || [],
      });
    } catch (err) {
      console.error('Failed to fetch session options:', err);
    } finally {
      setLoadingOptions(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
    fetchGateConfig();
  }, [fetchSessions, fetchGateConfig]);

  // Compute allowed landing property IDs based on selected scope
  const allowedLandingPropertyIds = useMemo(() => {
    const props = availableOptions.properties;
    if (scopeType === 'all') {
      return new Set(props.map(p => p.id));
    }
    if (scopeType === 'districts') {
      return new Set(
        props.filter(p => selectedDistricts.includes(p.district)).map(p => p.id)
      );
    }
    if (scopeType === 'properties') {
      return new Set(selectedProperties);
    }
    if (scopeType === 'mixed') {
      return new Set(
        props.filter(p =>
          selectedDistricts.includes(p.district) || selectedProperties.includes(p.id)
        ).map(p => p.id)
      );
    }
    return new Set();
  }, [scopeType, selectedDistricts, selectedProperties, availableOptions.properties]);

  // Reset landing property if it's no longer allowed by the scope
  useEffect(() => {
    if (landingPropertyId && !allowedLandingPropertyIds.has(landingPropertyId)) {
      setLandingPropertyId(null);
    }
  }, [landingPropertyId, allowedLandingPropertyIds]);

  // Fetch options when create form opens
  useEffect(() => {
    if (showCreateForm) {
      fetchOptions();
    }
  }, [showCreateForm, fetchOptions]);

  const handleToggle = async () => {
    setToggling(true);
    setError('');
    setSuccess('');
    try {
      const result = await setSessionGateEnabled(!gateEnabled);
      setGateEnabled(result.enabled);
      setSuccess(result.enabled ? t('session.gateEnabledMsg') : t('session.gateDisabledMsg'));
    } catch (err) {
      setError(err.message);
    } finally {
      setToggling(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    setSuccess('');
    setCreatedSession(null);

    // Build landing path: if a property is selected, point to that property detail page
    const landing_path = landingPropertyId
      ? `/housing/${landingPropertyId}`
      : '/';

    // Build scope payload based on scope type
    const scopePayload = {
      scope_type: scopeType,
      scope_districts: (scopeType === 'districts' || scopeType === 'mixed') ? selectedDistricts : [],
      scope_properties: (scopeType === 'properties' || scopeType === 'mixed') ? selectedProperties : [],
      landing_path,
    };

    try {
      const result = await createSession(newTtl, newLabel, scopePayload);
      setCreatedSession(result);
      setSuccess(t('session.createdSuccess'));
      setShowCreateForm(false);
      setNewLabel('');
      setNewTtl(60);
      setScopeType('all');
      setSelectedDistricts([]);
      setSelectedProperties([]);
      // Refresh list
      await fetchSessions();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (sessionId) => {
    if (!window.confirm(t('session.confirmRevoke'))) return;

    setError('');
    setSuccess('');
    try {
      await revokeSession(sessionId);
      setSuccess(t('session.revokedSuccess'));
      await fetchSessions();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRenew = async (sessionId) => {
    if (!window.confirm(t('session.renewConfirm', { ttl: '1 hour' }))) return;

    setError('');
    setSuccess('');
    try {
      await renewSession(sessionId, 60);
      setSuccess(t('session.renewSuccess'));
      await fetchSessions();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (sessionId) => {
    if (!window.confirm(t('session.deleteConfirm'))) return;

    setError('');
    setSuccess('');
    try {
      await hardDeleteSession(sessionId);
      setSuccess(t('session.deleteSuccess'));
      await fetchSessions();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCopyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId('url-' + Date.now());
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // Fallback
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedId('url-' + Date.now());
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('vi-VN', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const getStatusBadge = (session) => {
    if (!session.is_active) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">{t('session.revoked')}</span>;
    }
    const now = new Date();
    const expiresAt = new Date(session.expires_at);
    if (now > expiresAt) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700">{t('session.expired')}</span>;
    }
    return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">{t('session.active')}</span>;
  };

  const getTimeRemaining = (expiresAt) => {
    const now = new Date();
    const exp = new Date(expiresAt);
    const diffMs = exp.getTime() - now.getTime();
    if (diffMs <= 0) return t('session.expired');

    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 60) return t('session.minutesRemaining', { count: minutes });
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours < 24) return `${hours}h ${mins}m`;
    const days = Math.floor(hours / 24);
    return `${days} ${t('session.days')} ${hours % 24}h`;
  };

  const getScopeLabel = (session) => {
    const st = session.scope_type || 'all';
    const districts = session.scope_districts || [];
    const properties = session.scope_properties || [];

    if (st === 'all') {
      return <span className="text-xs text-gray-500">{t('session.scopeLabel_all')}</span>;
    }
    if (st === 'districts') {
      return <span className="text-xs text-primary-600">{t('session.scopeLabel_districts', { count: districts.length })}</span>;
    }
    if (st === 'properties') {
      return <span className="text-xs text-primary-600">{t('session.scopeLabel_properties', { count: properties.length })}</span>;
    }
    if (st === 'mixed') {
      return <span className="text-xs text-primary-600">{t('session.scopeLabel_mixed', { count: districts.length, props: properties.length })}</span>;
    }
    return <span className="text-xs text-gray-500">{t('session.scopeLabel_all')}</span>;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <AdminBreadcrumb />

        <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('session.pageTitle')}</h1>
              <p className="text-sm text-gray-500 mt-1">{t('session.pageSubtitle')}</p>
            </div>
            <button
              onClick={() => {
                setShowCreateForm(!showCreateForm);
                setCreatedSession(null);
                setError('');
              }}
              className="flex items-center gap-2 bg-primary-600 text-white px-5 py-2.5 rounded-lg hover:bg-primary-700 transition-colors font-medium"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              {t('session.createNew')}
            </button>
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 rounded-lg p-4">
              {error}
            </div>
          )}
          {success && !createdSession && (
            <div className="mb-6 bg-green-50 border border-green-200 text-green-700 rounded-lg p-4">
              {success}
            </div>
          )}

          {/* Global Toggle: Enable/Disable Session Gate */}
          <div className="mb-6 bg-white rounded-xl p-5 border border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-base font-semibold text-gray-900">{t('session.gateToggleTitle')}</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {gateEnabled ? t('session.gateEnabledDesc') : t('session.gateDisabledDesc')}
                </p>
              </div>
              <button
                onClick={handleToggle}
                disabled={toggling}
                className={`
                  relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
                  transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                  ${gateEnabled ? 'bg-primary-600' : 'bg-gray-200'}
                  ${toggling ? 'opacity-50 cursor-not-allowed' : ''}
                `}
                role="switch"
                aria-checked={gateEnabled}
              >
                <span
                  className={`
                    pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0
                    transition duration-200 ease-in-out
                    ${gateEnabled ? 'translate-x-5' : 'translate-x-0'}
                  `}
                />
              </button>
            </div>
            {toggling && (
              <div className="mt-2 flex items-center gap-2 text-sm text-gray-400">
                <div className="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
                <span>{t('common.updating')}</span>
              </div>
            )}
          </div>

          {/* Create Form */}
          {showCreateForm && (
            <div className="mb-6 bg-gray-50 rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('session.createFormTitle')}</h3>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('session.label')}</label>
                  <input
                    type="text"
                    value={newLabel}
                    onChange={(e) => setNewLabel(e.target.value)}
                    placeholder={t('session.labelPlaceholder')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('session.ttl')}</label>
                  <select
                    value={newTtl}
                    onChange={(e) => setNewTtl(parseInt(e.target.value))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white"
                  >
                    {SESSION_TTL_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{t(opt.label)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('session.landingProperty')}</label>
                  {loadingOptions ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
                      <div className="w-4 h-4 border-2 border-primary-300 border-t-primary-500 rounded-full animate-spin" />
                      {t('session.scopeLoading')}
                    </div>
                  ) : (
                    <div>
                      <AutocompleteSelect
                        options={[
                          { id: '', label: t('session.landingDefaultLabel'), group: '' },
                          ...availableOptions.properties.map(p => ({
                            id: p.id,
                            label: p.name,
                            group: p.district || 'Unknown',
                          }))
                        ]}
                        selected={landingPropertyId ? [landingPropertyId] : []}
                        onChange={(ids) => setLandingPropertyId(ids.length > 0 ? ids[0] : null)}
                        placeholder={t('session.landingPropertyPlaceholder')}
                        emptyText={t('session.scopeNoOptions')}
                        maxHeight="max-h-64"
                      />
                      <p className="text-xs text-gray-400 mt-1">{t('session.landingPropertyHint')}</p>
                    </div>
                  )}
                </div>

                {/* Scope Selection Section */}
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-base font-semibold text-gray-900 mb-3">{t('session.scopeSection')}</h4>

                  {/* Scope Type Selector */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                    <button
                      type="button"
                      onClick={() => {
                        setScopeType('all');
                        setSelectedDistricts([]);
                        setSelectedProperties([]);
                      }}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        scopeType === 'all'
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-medium text-sm">{t('session.scopeAll')}</div>
                      <div className="text-xs text-gray-500 mt-1">{t('session.scopeAllDesc')}</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setScopeType('districts');
                        setSelectedProperties([]);
                      }}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        scopeType === 'districts'
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-medium text-sm">{t('session.scopeDistricts')}</div>
                      <div className="text-xs text-gray-500 mt-1">{t('session.scopeDistrictsDesc')}</div>
                      {scopeType === 'districts' && selectedDistricts.length > 0 && (
                        <div className="text-xs text-primary-600 font-medium mt-1">
                          {t('session.scopeSelected', { count: selectedDistricts.length })}
                        </div>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setScopeType('properties');
                        setSelectedDistricts([]);
                      }}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        scopeType === 'properties'
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-medium text-sm">{t('session.scopeProperties')}</div>
                      <div className="text-xs text-gray-500 mt-1">{t('session.scopePropertiesDesc')}</div>
                      {scopeType === 'properties' && selectedProperties.length > 0 && (
                        <div className="text-xs text-primary-600 font-medium mt-1">
                          {t('session.scopeSelected', { count: selectedProperties.length })}
                        </div>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setScopeType('mixed')}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        scopeType === 'mixed'
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-medium text-sm">{t('session.scopeMixed')}</div>
                      <div className="text-xs text-gray-500 mt-1">{t('session.scopeMixedDesc')}</div>
                      {scopeType === 'mixed' && (selectedDistricts.length > 0 || selectedProperties.length > 0) && (
                        <div className="text-xs text-primary-600 font-medium mt-1">
                          {t('session.scopeSelected', { count: selectedDistricts.length + selectedProperties.length })}
                        </div>
                      )}
                    </button>
                  </div>

                  {/* District Selection with Autocomplete */}
                  {scopeType === 'districts' && (
                    <div>
                      {loadingOptions ? (
                        <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
                          <div className="w-4 h-4 border-2 border-primary-300 border-t-primary-500 rounded-full animate-spin" />
                          {t('session.scopeLoading')}
                        </div>
                      ) : availableOptions.districts.length === 0 ? (
                        <p className="text-sm text-gray-500 py-4">{t('session.scopeNoOptions')}</p>
                      ) : (
                        <div>
                          <p className="text-sm text-gray-600 mb-2">{t('session.scopeSelectDistricts')}</p>
                          <AutocompleteSelect
                            options={availableOptions.districts.map(d => ({
                              id: d.name,
                              label: `${d.name} (${d.propertyCount})`,
                              group: '',
                            }))}
                            selected={selectedDistricts}
                            onChange={setSelectedDistricts}
                            placeholder={t('session.scopeSelectDistricts')}
                            emptyText={t('session.scopeNoOptions')}
                            maxHeight="max-h-48"
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Mixed Selection: show BOTH district and property selectors */}
                  {scopeType === 'mixed' && (
                    <div className="space-y-4">
                      {loadingOptions ? (
                        <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
                          <div className="w-4 h-4 border-2 border-primary-300 border-t-primary-500 rounded-full animate-spin" />
                          {t('session.scopeLoading')}
                        </div>
                      ) : (
                        <>
                          <div>
                            <p className="text-sm text-gray-600 mb-2">{t('session.scopeSelectDistricts')}</p>
                            <AutocompleteSelect
                              options={availableOptions.districts.map(d => ({
                                id: d.name,
                                label: `${d.name} (${d.propertyCount})`,
                                group: '',
                              }))}
                              selected={selectedDistricts}
                              onChange={setSelectedDistricts}
                              placeholder={t('session.scopeSelectDistricts')}
                              emptyText={t('session.scopeNoOptions')}
                              maxHeight="max-h-48"
                            />
                          </div>
                          <div>
                            <p className="text-sm text-gray-600 mb-2">{t('session.scopeSelectProperties')}</p>
                            <AutocompleteSelect
                              options={availableOptions.properties.map(p => ({
                                id: p.id,
                                label: p.name,
                                group: p.district || 'Unknown',
                              }))}
                              selected={selectedProperties}
                              onChange={setSelectedProperties}
                              placeholder={t('session.scopeSelectProperties')}
                              emptyText={t('session.scopeNoOptions')}
                              maxHeight="max-h-64"
                            />
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Property Selection with Autocomplete */}
                  {scopeType === 'properties' && (
                    <div>
                      {loadingOptions ? (
                        <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
                          <div className="w-4 h-4 border-2 border-primary-300 border-t-primary-500 rounded-full animate-spin" />
                          {t('session.scopeLoading')}
                        </div>
                      ) : availableOptions.properties.length === 0 ? (
                        <p className="text-sm text-gray-500 py-4">{t('session.scopeNoOptions')}</p>
                      ) : (
                        <div>
                          <p className="text-sm text-gray-600 mb-2">{t('session.scopeSelectProperties')}</p>
                          <AutocompleteSelect
                            options={availableOptions.properties.map(p => ({
                              id: p.id,
                              label: p.name,
                              group: p.district || 'Unknown',
                            }))}
                            selected={selectedProperties}
                            onChange={setSelectedProperties}
                            placeholder={t('session.scopeSelectProperties')}
                            emptyText={t('session.scopeNoOptions')}
                            maxHeight="max-h-64"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Destination Property Selection */}
                <div className="border-t border-gray-200 pt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('session.landingProperty')}</label>
                    {loadingOptions ? (
                      <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
                        <div className="w-4 h-4 border-2 border-primary-300 border-t-primary-500 rounded-full animate-spin" />
                        {t('session.scopeLoading')}
                      </div>
                    ) : (
                      <div>
                        <AutocompleteSelect
                          options={[
                            { id: '', label: t('session.landingDefaultLabel'), group: '' },
                            ...availableOptions.properties
                              .filter(p => allowedLandingPropertyIds.has(p.id))
                              .map(p => ({
                                id: p.id,
                                label: p.name,
                                group: p.district || 'Unknown',
                              }))
                          ]}
                          selected={landingPropertyId ? [landingPropertyId] : []}
                          onChange={(ids) => setLandingPropertyId(ids.length > 0 ? ids[0] : null)}
                          placeholder={t('session.landingPropertyPlaceholder')}
                          emptyText={t('session.scopeNoOptions')}
                          maxHeight="max-h-64"
                        />
                        <p className="text-xs text-gray-400 mt-1">{t('session.landingPropertyHint')}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={creating || 
                      (scopeType === 'districts' && selectedDistricts.length === 0) || 
                      (scopeType === 'properties' && selectedProperties.length === 0) ||
                      (scopeType === 'mixed' && selectedDistricts.length === 0 && selectedProperties.length === 0)}
                    className="flex-1 bg-primary-600 text-white py-2.5 px-6 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 font-medium"
                  >
                    {creating ? t('session.creating') : t('session.generateLink')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateForm(false);
                      setCreatedSession(null);
                      setError('');
                      setScopeType('all');
                      setSelectedDistricts([]);
                      setSelectedProperties([]);
                      setLandingPropertyId(null);
                    }}
                    className="px-6 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Created Session Result */}
          {createdSession && (
            <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <h3 className="font-semibold text-green-800">{t('session.sessionCreated')}</h3>
              </div>
              <div className="bg-white rounded-lg p-4 border border-green-100">
                <p className="text-sm text-gray-500 mb-2">{t('session.accessUrl')}</p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={createdSession.access_url}
                    className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-mono text-gray-700"
                  />
                  <button
                    onClick={() => handleCopyUrl(createdSession.access_url)}
                    className="flex items-center gap-1.5 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors text-sm"
                  >
                    {copiedId === 'url-' + Date.now() ? (
                      <>
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {t('common.copied')}
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        {t('common.copy')}
                      </>
                    )}
                  </button>
                </div>
                {/* Show scope info */}
                {createdSession.scope_type && createdSession.scope_type !== 'all' && (
                  <div className="mt-3">
                    <span className="text-xs text-gray-500">
                      {t('session.scopeInfo', {
                        scope: createdSession.scope_type === 'districts'
                          ? t('session.scopeLabel_districts', { count: (createdSession.scope_districts || []).length })
                          : createdSession.scope_type === 'properties'
                            ? t('session.scopeLabel_properties', { count: (createdSession.scope_properties || []).length })
                            : t('session.scopeLabel_mixed', { count: (createdSession.scope_districts || []).length, props: (createdSession.scope_properties || []).length })
                      })}
                    </span>
                  </div>
                )}
                <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">{t('session.label')}:</span>
                    <span className="ml-2 text-gray-800 font-medium">{createdSession.label || t('session.noLabel')}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">{t('session.expiresAt')}:</span>
                    <span className="ml-2 text-gray-800 font-medium">{formatDate(createdSession.expires_at)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Sessions Table */}
          {loading ? (
            <div className="text-center py-12">
              <div className="w-10 h-10 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin mx-auto mb-4" />
              <p className="text-gray-500">{t('session.loading')}</p>
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">{t('session.noSessions')}</h3>
              <p className="text-sm text-gray-500">{t('session.noSessionsHint')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">{t('session.label')}</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">{t('session.created')}</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">{t('session.expires')}</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">{t('session.timeRemaining')}</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">{t('session.status')}</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">{t('session.accessCount')}</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">Scope</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-600">{t('session.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sessions.map((session) => (
                    <tr key={session.id} className="hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4">
                        <span className="font-medium text-gray-900">{session.label || t('session.noLabel')}</span>
                      </td>
                      <td className="py-3 px-4 text-gray-600 whitespace-nowrap">{formatDate(session.created_at)}</td>
                      <td className="py-3 px-4 text-gray-600 whitespace-nowrap">{formatDate(session.expires_at)}</td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        <span className={new Date(session.expires_at) <= new Date() ? 'text-red-600' : 'text-gray-700'}>
                          {getTimeRemaining(session.expires_at)}
                        </span>
                      </td>
                      <td className="py-3 px-4">{getStatusBadge(session)}</td>
                      <td className="py-3 px-4 text-gray-600">{session.access_count || 0}</td>
                      <td className="py-3 px-4">{getScopeLabel(session)}</td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {/* Copy URL button - always available for active+non-expired */}
                          {session.is_active && new Date(session.expires_at) > new Date() && (
                            <button
                              onClick={() => handleCopyUrl(`${window.location.origin}?session=${session.token}`)}
                              className="text-sm px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                              title={t('common.copy')}
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </button>
                          )}
                          {/* Revoke button - only for active+non-expired */}
                          {session.is_active && new Date(session.expires_at) > new Date() && (
                            <button
                              onClick={() => handleRevoke(session.id)}
                              className="text-sm px-3 py-1.5 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors"
                            >
                              {t('session.revoke')}
                            </button>
                          )}
                          {/* Renew button - for expired or revoked sessions */}
                          {(!session.is_active || new Date(session.expires_at) <= new Date()) && (
                            <button
                              onClick={() => handleRenew(session.id)}
                              className="text-sm px-3 py-1.5 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors"
                            >
                              {t('session.renew')}
                            </button>
                          )}
                          {/* Delete button - always available for any session */}
                          <button
                            onClick={() => handleDelete(session.id)}
                            className="text-sm px-3 py-1.5 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors"
                          >
                            {t('session.deleteSession')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SessionManagerPage;